import dynamic from "next/dynamic";
import Link from "next/link";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import MethodologyAnchor from "../../components/services/MethodologyAnchor";
import Head from "next/head";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import OrchestriкSection from "../../components/services/OrchestriкSection";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";

const ContactUsHome = dynamic(() => import("../../components/homepage/ContactUsHome"), { ssr: false });
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

Analyticsaiautomation.title = "AI & Analytics Consulting: Built, Not Advised | ITMTB";
Analyticsaiautomation.desc =
  "Enterprise AI agents built, deployed, and operated in production, not slide decks. Proven analytics and automation: 99% faster lookups across 100,000+ SKUs.";
Analyticsaiautomation.header_img = "https://www.itmtb.com/services/capability/data.webp";
Analyticsaiautomation.keywords =
  "AI agent deployment, enterprise AI consulting, agentic AI implementation, AI automation services, analytics consulting, LLM deployment enterprise, machine learning solutions, AI-driven automation, intelligent automation solutions";

const faqItems = [
  {
    q: "What is an AI agent deployment partner?",
    a: "An AI agent deployment partner scopes, builds, deploys, and operates AI agents for enterprise workflows — end to end. This is different from an 'AI consultant' who advises but doesn't build, and different from a product vendor who sells a licensed platform. ITMTB designs the architecture (model selection, orchestration framework, tool integration), builds the production system (using technologies like LangChain, LlamaIndex, GPT-4, Claude, and RAG pipelines), deploys it into your infrastructure, and operates it post-launch. The output is a running AI system in your environment, not a slide deck or a SaaS subscription.",
  },
  {
    q: "What AI use cases deliver the fastest ROI?",
    a: "Based on ITMTB's delivery experience, the use cases with the fastest return are: (1) Document and data processing automation — invoices, contracts, reports that currently require manual review. (2) Supply chain pattern matching and lookup — we delivered a 99% efficiency gain for a global supply chain leader managing 100,000+ SKUs, reducing lookup time from hours to under 3 minutes. (3) Risk and compliance report generation — fully automated third-party risk reports for a global intelligence firm, replacing hours of analyst work per report. (4) Customer-service and internal-support agents — handling tier-1 queries with tool-calling agents that reach into live systems. These use cases share a common trait: there is a well-defined, high-volume manual process with structured-enough data to give an agent reliable inputs.",
  },
  {
    q: "How long does an AI project take from scoping to production?",
    a: "ITMTB engagements follow a fixed shape. Discovery sprint: 2 weeks — we scope the problem, estimate cost and timeline, and identify technical and data risks. You receive a written roadmap and risk register before committing to the build. MVP build: 4 to 8 weeks, depending on integration complexity and data readiness. Production deployment: 8 to 16 weeks for full production-grade systems including testing, monitoring, and handoff. Total calendar time from first conversation to a live production system typically runs 12 to 20 weeks. The primary variable is data readiness — teams with clean, accessible data move faster.",
  },
  {
    q: "How do you handle AI hallucination and reliability in production?",
    a: "This is the right question to ask before choosing a build partner. Our production AI systems use four layers of reliability: (1) Retrieval-Augmented Generation (RAG) — the agent retrieves facts from a controlled, versioned knowledge base rather than relying on model memory, which eliminates whole classes of hallucination. (2) Output validation — structured output schemas, constraint checking, and deterministic guard rails on every agent response. (3) Human-in-the-loop escalation — for high-stakes decisions, agents are designed to escalate to a human reviewer rather than auto-act when confidence is below threshold. (4) Monitoring and regression testing — production agents are monitored for output drift, prompt injection attempts, and latency degradation. Reliability in production is an architecture decision, not a tuning afterthought.",
  },
  {
    q: "How do you handle data privacy and compliance in enterprise AI projects?",
    a: "Compliance is scoped in the discovery sprint — not retrofitted. For every engagement we document data flows, identify which data categories are processed by the AI system, and design the appropriate controls: data residency within the required region, PII segregation and access logging, consent management where needed, and audit trails for AI decisions that can be produced for a regulatory or internal review. We have delivered AI systems for clients operating under financial services regulations, data protection laws, and enterprise information security policies. The specific controls depend on your jurisdiction and data category — we identify them during discovery.",
  },
  {
    q: "How does ITMTB price an engagement?",
    a: "Pricing follows the engagement model. Discovery sprints are fixed-price for a fixed timebox. Build engagements are scoped after discovery — fixed-scope, time-and-materials, or outcome-linked. Managed services are billed monthly with defined SLAs. Total cost depends on scope, integration complexity, QA depth, and post-launch support — we publish a transparent breakdown for buyers evaluating their options.",
    link: "/blog/custom-software-development-cost-india",
    linkLabel: "Read the cost guide",
  },
  {
    q: "What does an engagement with ITMTB look like?",
    a: "Every engagement starts with a fixed-scope, fixed-price discovery sprint — typically 2 weeks. You leave with a written roadmap, risk register, cost estimate, and timeline before committing to a build. We then offer four engagement models: fixed-scope builds, time-and-materials, outcome-linked retainers, and managed services post-launch. If discovery doesn't justify proceeding, you can stop there — no obligation.",
    link: "/itmtb-how-we-work",
    linkLabel: "See how we work",
  },
];
Analyticsaiautomation.faq = faqItems;

function Analyticsaiautomation() {
  let insights;
  let caseStudies;

  let aiInsights = [...blogData].filter(
    item =>
      item?.meta?.refer?.includes("Data and Artificial Intelligence") &&
      !["case-study", "tech"].includes(item?.meta?.category)
  );
  let aiCaseStudies = [...blogData].filter(
    item =>
      item?.meta?.refer?.includes("Data and Artificial Intelligence") &&
      item?.meta?.category === "case-study"
  );

  if (aiInsights.length >= 2) {
    insights = getRandom(aiInsights, 2);
  } else if (aiInsights.length === 1) {
    insights = [
      ...aiInsights,
      ...getRandom(
        [...blogData].filter(
          item =>
            !["case-study", "tech"].includes(item?.meta?.category) &&
            !aiInsights.includes(item)
        ),
        1
      ),
    ];
  } else {
    insights = getRandom(
      [...blogData].filter(item => !["case-study", "tech"].includes(item?.meta?.category)),
      2
    );
  }

  if (aiCaseStudies.length >= 2) {
    caseStudies = getRandom(aiCaseStudies, 2);
  } else {
    caseStudies = getRandom(
      [...blogData].filter(item => item?.meta?.category === "case-study"),
      2
    );
  }

  const subServices = [
    {
      img: null,
      key: "Not sure where AI fits? Start with a strategy session.",
      title: "A focused 2-week engagement that tells you exactly what AI can do for your operations — and what it can't.",
      text: "Before committing to a build, you walk away with a clear picture: which workflows are worth automating, a realistic cost and timeline, and a written risk register. Fixed scope, fixed price. No open-ended retainer, no obligation to proceed.\n\n<a href='/contact-us' style='color:#93c5fd;font-weight:600;text-decoration:underline;'>Talk to us →</a>",
    },
    {
      img: null,
      key: "AI Agents That Run Your Workflows End to End",
      title: "Your team stops managing the process. The agent handles approvals, lookups, decisions, and notifications — autonomously.",
      text: "Think of the workflows your team coordinates manually today: gathering approvals, pulling data from multiple systems, routing tasks, sending updates. An AI agent can run the entire sequence — acting on live data, following your business rules, and escalating to a human only when it genuinely needs to.\n\nWe design and build these agents to fit your existing systems and processes. No rip-and-replace. The agent connects to what you already have.",
    },
    {
      img: null,
      key: "Secure, Auditable, and Compliant Deployments",
      title: "Every AI decision is logged, every access is controlled, and every deployment can run in your own environment.",
      text: "Enterprise AI must meet the same standards as any other production system — and usually higher ones. We build in role-based access controls, full audit trails for every agent decision, data residency within your required region, and PII segregation from the start.\n\nFor organisations that cannot send data to third-party cloud services, we also support on-premises and private cloud deployments. Compliance requirements are scoped in the discovery sprint — not retrofitted after the fact.",
    },
    {
      img: null,
      key: "Works With What You Already Have",
      title: "Your AI agents connect to your ERP, CRM, databases, and legacy systems — not a separate data island.",
      text: "The biggest risk in any AI project is building something that can't reach your real operational data. We spend time upfront mapping your systems — whether that's SAP, Dynamics 365, Salesforce, a proprietary database, or a set of internal APIs — and designing the integration layer so agents can act on live, accurate information.\n\nIf a legacy system doesn't have an API, we've built bridges before. Your AI investment should work with your existing technology, not require you to replace it.",
    },
    {
      img: null,
      key: "24×7 Agent Operations and Support",
      title: "We don't disappear after deployment. Your agents are monitored, maintained, and supported around the clock.",
      text: "Running AI agents in production is different from running traditional software. Model drift, prompt injection risks, API dependency changes, and inference cost spikes all require specialist ops. We provide:\n\n• 24×7 availability monitoring with alerting\n• Incident response SLA for production issues\n• Regular model performance reviews and prompt regression testing\n• A warranty period post-handoff — bugs and regressions fixed at zero cost\n\nYou get a system that stays reliable, not one you have to babysit.",
    },
    {
      img: null,
      key: "Accurate AI Starts With Reliable Data",
      title: "An AI that sees bad data makes confident mistakes. We make sure yours never does.",
      text: "Before any agent goes live, we audit and fix the data it will rely on. That means pipelines that pull from your source systems, clean and structure the data correctly, and keep it fresh as your business changes.\n\nWe also build monitoring so that if data quality degrades — a schema change, a broken feed, a missing field — you find out before your AI does.",
    },
    {
      img: null,
      key: "Multi-Agent Systems for Complex Processes",
      title: "When one agent isn't enough — we build systems where multiple specialized agents coordinate, hand off, and escalate.",
      text: "Some business processes are too complex for a single agent: they require specialist judgment at different stages, approvals from multiple parties, or coordination across departments. Multi-agent systems handle this by assigning each stage to a purpose-built agent, with defined handoff points and escalation paths.\n\nWe've built multi-agent pipelines for document processing chains, risk assessment workflows, and operational coordination systems. The architecture is designed so each agent is testable and auditable independently.",
    },
  ];

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              name: "AI Agent Deployment & Analytics Consulting — Enterprise India",
              url: "https://www.itmtb.com/services/analytics-ai-automation",
              image: "https://www.itmtb.com/services/capability/data.webp",
              description:
                "ITMTB deploys AI agents for enterprise operations — analytics, automation, and agentic AI built for production. Enterprise AI deployment partner.",
              provider: {
                "@type": "Organization",
                name: "ITMTB Technologies",
                url: "https://www.itmtb.com",
                logo: "https://www.itmtb.com/logo.webp",
              },
              areaServed: [{ "@type": "Place", name: "India" }],
              serviceType: [
                "AI Agent Deployment",
                "Enterprise AI Consulting",
                "Agentic AI Implementation",
                "AI Automation Services",
                "Analytics Consulting",
                "LLM Deployment Enterprise",
                "Machine Learning Solutions",
                "Intelligent Automation",
              ],
              sameAs: [
                "https://www.linkedin.com/company/itmtb-technologies/",
                "https://x.com/ITMTBTech",
                "https://www.instagram.com/itmtbtech/",
              ],
            }),
          }}
        />
      </Head>

      <Service2
        heading="AI Agents for Enterprise Operations"
        paragraph={`Your team spends hours on decisions and tasks that follow predictable rules — reviewing documents, chasing approvals, matching records, generating reports. AI agents can run these workflows autonomously, acting on your data and escalating only when a human genuinely needs to decide.

We've delivered this in production: a 99% efficiency gain for a global supply chain firm, and fully automated risk reporting for a global intelligence firm — replacing hours of analyst work per report.

ITMTB builds the full stack — scoping what's worth automating, building production-grade AI agents, deploying into your environment, and operating post-launch.`}
        bgPath="/services/capability/data.webp"
        primaryCta={{ label: "Talk to us →", href: "/contact-us" }}
      />

      <SuccessStoryEmbed
        referTag="Data and Artificial Intelligence"
        maxItems={3}
        heading="Selected work in AI & automation"
      />

      <section className="px-8 md:px-[60px] py-8 bg-white border-t border-gray-100">
        <p className="max-w-4xl text-sm text-gray-500 leading-relaxed">
          For regulated document workflows, see how we designed a{" "}
          <Link href="/blog/pharma-compliance-ai-sop-bmr-warning-letter-review">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">
              pharma compliance AI platform for SOP, BMR, and warning-letter review
            </a>
          </Link>{" "}
          with traceable findings and human review built into the process.
        </p>
        <p className="max-w-4xl text-sm text-gray-500 leading-relaxed mt-3">
          Before an assistant or agent can reason over your content, it has to retrieve it. See what we found
          testing{" "}
          <Link href="/blog/enterprise-search-real-world-queries">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">
              enterprise search against real-world queries
            </a>
          </Link>{" "}
          in a catalogue where more than half of all searches were returning nothing.
        </p>
        <p className="max-w-4xl text-sm text-gray-500 leading-relaxed mt-3">
          Need one difficult use case taken all the way to production? Our{" "}
          <Link href="/services/forward-deployed-engineering">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">
              forward deployed engineering
            </a>
          </Link>{" "}
          model embeds a senior pod with your team — from workflow discovery to secure deployment — and starts with a fixed-scope AI Deployment Readiness Sprint.
        </p>
      </section>

      <CustomerSection title="Trusted by" />

      <OrchestriкSection />

      <MethodologyAnchor />

      <Capabilities2
        dataList={subServices}
        bg="bg-[#402A6A]"
        insights={[...caseStudies, ...insights]}
        hideFooter
        hideCardSubtitle
      />

      <ServiceFAQ items={faqItems} />

      <ServiceEngagementLinks />

      <ContactUsHome />

      <Footer />
    </div>
  );
}

export default Analyticsaiautomation;
